import { Dispatch, SetStateAction } from "react";
import { SetterOrUpdater } from "recoil";

import {
  RequisitionModelSerializer,
  RequisitionRow,
  UserDefaultRequisitionsResponse,
  UserSearchRequisitionsResponse,
} from "../../recoil/types";
import {
  convertRequisitionToRequisitionRow,
  searchCharacterCountValid,
} from "../../recoil/helpers";
import {
  GetArchivedRequisitions,
  RefreshNewRequisitionOrders,
  RefreshRequisitionRows,
} from "./Services/GridService";
import { NewRequisitionType } from "../NewRequisition/types";

interface UseRequisitionRefreshParams {
  token?: string;
  setLoggedOut: (inactivity: boolean) => void;
  canUseProviderCfc: boolean;
  searchText: string;
  setRequisitions: SetterOrUpdater<UserDefaultRequisitionsResponse | undefined>;
  setSearchRequisitions: SetterOrUpdater<
    UserSearchRequisitionsResponse | undefined
  >;
  setArchivedRequisitions: Dispatch<SetStateAction<RequisitionRow[]>>;
  setAllOrderedRequisitions: SetterOrUpdater<NewRequisitionType[]>;
  setOrderedRequestComplete: SetterOrUpdater<boolean>;
}

export interface RequisitionRefresh {
  refreshSearchResults: () => Promise<void>;
  refreshDefaultAndArchivedRequisitions: () => Promise<void>;
  refreshVisibleRequisitionData: () => Promise<void>;
  refreshAfterCfcOrderSubmitted: () => void;
}

export const useRequisitionRefresh = ({
  token,
  setLoggedOut,
  canUseProviderCfc,
  searchText,
  setRequisitions,
  setSearchRequisitions,
  setArchivedRequisitions,
  setAllOrderedRequisitions,
  setOrderedRequestComplete,
}: UseRequisitionRefreshParams): RequisitionRefresh => {
  const refreshSearchResults = async (): Promise<void> => {
    if (!token || !searchCharacterCountValid(searchText)) return;
    await RefreshRequisitionRows(
      token,
      setSearchRequisitions,
      setLoggedOut,
      searchText,
    );
  };

  const refreshDefaultAndArchivedRequisitions = async (): Promise<void> => {
    if (!token) return;
    await RefreshRequisitionRows(token, setRequisitions, setLoggedOut);
    const archivedResponse = await GetArchivedRequisitions(token, setLoggedOut);
    if (archivedResponse.ok) {
      const data = await archivedResponse.json();
      setArchivedRequisitions(
        (data.requisitions_archived || []).map(
          (req: RequisitionModelSerializer, index: number) =>
            convertRequisitionToRequisitionRow(req, index, canUseProviderCfc),
        ),
      );
    }
  };

  const refreshVisibleRequisitionData = async (): Promise<void> => {
    await refreshDefaultAndArchivedRequisitions().catch(() => {});
    await refreshSearchResults().catch(() => {});
  };

  const refreshAfterCfcOrderSubmitted = (): void => {
    if (!token) return;

    const refreshRequests = [
      RefreshRequisitionRows(token, setRequisitions, setLoggedOut),
      RefreshNewRequisitionOrders(
        token,
        setAllOrderedRequisitions,
        setLoggedOut,
        setOrderedRequestComplete,
      ),
    ];

    if (searchCharacterCountValid(searchText)) {
      refreshRequests.push(refreshSearchResults());
    }

    Promise.all(refreshRequests).catch(() => {});
  };

  return {
    refreshSearchResults,
    refreshDefaultAndArchivedRequisitions,
    refreshVisibleRequisitionData,
    refreshAfterCfcOrderSubmitted,
  };
};
